/**
 * Obsidian black cut, as sRGB hex, for the derived assets that cannot read
 * CSS custom properties: the edge OG raster (src/app/opengraph-image.tsx)
 * and the Shiki syntax themes (src/lib/shiki-obsidian.ts).
 *
 * Every value here is a copy of a variable in the vendored identity package
 * src/app/dcyfr-theme-obsidian.css (the `[data-identity="obsidian"]` and
 * `[data-identity="obsidian"].dark` base blocks, which are the black cut),
 * or the sRGB conversion of a derived OKLCH value declared once in
 * src/app/globals.css's obsidian-scoped extension blocks. The site renders
 * the black cut by default; the other four cuts re-author only the accent
 * ramp, the field group, --border and the sheen, which these assets do not
 * draw.
 *
 * tests/lib/obsidian-palette.test.ts recomputes this table from the CSS and
 * fails when the two disagree, so a palette change in the registry cannot
 * leave the OG image or a code block on the previous identity.
 */

/** Light scheme: the stone on the jeweller's cloth. */
export const OBSIDIAN_LIGHT = {
  background: '#f5efe9',
  foreground: '#08090e',
  card: '#fcf9f7',
  mutedForeground: '#605a54',
  secure: '#1a47b5',
  success: '#0d6039',
  warning: '#7d4f0c',
  destructive: '#9e1815',
} as const;

/** Dark scheme: the glass, the black cut ground and its lightness steps. */
export const OBSIDIAN_DARK = {
  background: '#08090e',
  foreground: '#ebe7e4',
  card: '#131419',
  popover: '#1c1e23',
  mutedForeground: '#a39d98',
  accent400: '#b0b1b4',
  secure: '#7aa6ff',
  success: '#5cd9a0',
  warning: '#ecb94b',
  destructive: '#f4654f',
  field: '#08090e',
  fieldForeground: '#e2ddd8',
  fieldAccent: '#816a52',
} as const;

/**
 * Derived steps with no contract variable, authored as OKLCH in globals.css
 * (`--syntax-operator` / `--syntax-punctuation` under the obsidian-scoped
 * blocks) and converted here: the silver ramp's hue and chroma
 * (0.004 275.6, from the package's @supports overlay) at a lightness
 * between the foreground and the scheme's secondary text step.
 */
export const OBSIDIAN_DERIVED = {
  /** oklch(35% 0.004 275.6) */
  syntaxOperatorLight: '#3a3a3d',
  /** oklch(87% 0.004 275.6) */
  syntaxOperatorDark: '#d3d4d7',
} as const;
